<!-- src/components/forms/completed/AccidentWitnessCompleted.vue -->
<template>
  <div class="bg-white p-2 sm:p-4 md:p-8 space-y-12 animate-fadeIn text-left min-h-screen">
    
    <!-- 1. Witness Status Header (PDF Ready) -->
    <div class="flex flex-wrap items-center justify-between gap-4 p-8 bg-slate-50 rounded-[32px] border border-slate-200 shadow-inner">
      <div class="flex items-center gap-4 text-left">
        <div class="bg-[#20FCF1] w-3 h-3 rounded-full animate-pulse shadow-[0_0_10px_rgba(56,227,176,0.5)]"></div>
        <div>
          <p class="text-[10px] font-black uppercase tracking-[0.2em] text-[#0A1630]">Record Type</p>
          <p class="text-sm font-bold text-slate-500 uppercase tracking-widest">Statutory Witness Testimony</p>
        </div>
      </div>
      <div class="bg-[#0A1630] px-6 py-3 rounded-2xl shadow-lg text-right">
        <p class="text-[9px] font-black uppercase tracking-widest text-[#20FCF1] mb-1">Linked Incident</p>
        <p class="text-sm font-black text-white tracking-widest">{{ submissionData?.accidentReference || 'REF-PENDING' }}</p>
      </div>
    </div>

    <!-- 1. Witness Identity -->
    <section>
      <div class="flex items-center gap-3 mb-8 text-left">
        <div class="bg-[#20FCF1] p-2.5 rounded-xl shadow-sm">
          <IdentificationIcon class="h-6 w-6 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">1. Witness Identity</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 bg-slate-50 p-8 rounded-[40px] border border-slate-100 shadow-inner">
        <div class="StatCard">
          <span class="label">Full Name</span>
          <span class="value">{{ submissionData?.witnessName || 'N/A' }}</span>
        </div>
        <div class="StatCard">
          <span class="label">Sentinel No.</span>
          <span class="value tracking-widest">{{ submissionData?.witnessSentinel || 'N/A' }}</span>
        </div>
        <div class="StatCard">
          <span class="label">Company Position</span>
          <span class="value">{{ typeof formatText === 'function' ? formatText(submissionData?.witnessPosition) : submissionData?.witnessPosition }}</span>
        </div>
        <div class="StatCard">
          <span class="label">Email Address</span>
          <span class="value lowercase text-xs">{{ submissionData?.witnessEmail || 'N/A' }}</span>
        </div>
        <div class="StatCard">
          <span class="label">Contact Phone</span>
          <span class="value">{{ submissionData?.witnessPhone || 'N/A' }}</span>
        </div>
        <div class="StatCard">
          <span class="label">Immediate Manager</span>
          <span class="value">{{ submissionData?.witnessManager || 'N/A' }}</span>
        </div>
      </div>
    </section>

    <!-- 2. Incident Context -->
    <section>
      <div class="flex items-center gap-3 mb-8 text-left">
        <div class="bg-[#20FCF1] p-2.5 rounded-xl shadow-sm">
          <CalendarDaysIcon class="h-6 w-6 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">2. Incident Context</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
        <div class="p-8 bg-white border-l-8 border-l-[#20FCF1] rounded-[32px] shadow-sm border border-slate-100">
          <p class="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-2">Date & Time of Accident</p>
          <p class="text-xl font-black text-[#0A1630]">{{ typeof formatDate === 'function' ? formatDate(submissionData?.accidentDateTime) : submissionData?.accidentDateTime }}</p>
        </div>
        <div class="p-8 bg-white border-l-8 border-l-[#0A1630] rounded-[32px] shadow-sm border border-slate-100">
          <p class="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-2">Occurrence Location</p>
          <p class="text-xl font-black text-[#0A1630] uppercase tracking-tight">{{ submissionData?.location || 'N/A' }}</p>
        </div>
      </div>
    </section>

    <!-- 3. Sworn Testimony -->
    <section>
      <div class="flex items-center gap-3 mb-8 text-left">
        <div class="bg-[#20FCF1] p-2.5 rounded-xl shadow-sm">
          <ChatBubbleBottomCenterTextIcon class="h-6 w-6 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">3. Sworn Testimony</h3>
      </div>
      
      <div class="bg-white border border-slate-200 rounded-[40px] p-10 shadow-sm relative overflow-hidden text-left">
        <div class="absolute top-0 left-0 w-2.5 h-full bg-[#20FCF1]"></div>
        <p class="text-slate-600 leading-[2] whitespace-pre-wrap italic font-medium text-lg">
          "{{ submissionData?.statement || 'No statement recorded.' }}"
        </p>
      </div>
    </section>

    <!-- 4. Declaration -->
    <section v-if="submissionData?.statementConfirmation">
      <div class="flex items-center gap-3 mb-8 text-left">
        <div class="bg-[#20FCF1] p-2.5 rounded-xl shadow-sm">
          <CheckBadgeIcon class="h-6 w-6 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">4. Declaration</h3>
      </div>
      <div class="p-8 bg-slate-900 rounded-[32px] text-white border-l-8 border-l-[#20FCF1] shadow-xl">
        <p class="text-xs font-black uppercase tracking-widest text-[#20FCF1] mb-2">Legal Fact Confirmation</p>
        <p class="text-sm font-medium italic text-slate-300">"I believe that the facts stated in this witness statement are true and accurate."</p>
      </div>
    </section>

    <!-- 5. Evidence Vault -->
    <section v-if="hasAttachments">
      <div class="flex items-center gap-3 mb-8 text-left">
        <div class="bg-[#20FCF1] p-2.5 rounded-xl shadow-sm">
          <PaperClipIcon class="h-6 w-6 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">5. Evidence Vault</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
        <!-- Photographic Grid -->
        <div v-if="submissionData?.imageUrls?.length" class="space-y-4">
          <p class="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-4">Photographic / Sketch Evidence</p>
          <div class="grid grid-cols-2 gap-4 p-8 bg-slate-50 rounded-[40px] border border-slate-100 shadow-inner">
            <div v-for="(url, idx) in submissionData.imageUrls" :key="idx" class="aspect-square overflow-hidden rounded-[24px] border-4 border-white shadow-lg">
              <img :src="url" class="h-full w-full object-cover" />
            </div>
          </div>
        </div>

        <!-- Document Registry -->
        <div v-if="submissionData?.documentUrls?.length || submissionData?.certificationUrls?.length" class="space-y-8">
          <div v-if="submissionData?.documentUrls?.length">
             <p class="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-4">Supporting Documents</p>
             <div class="space-y-2 mt-4">
               <div v-for="(url, idx) in submissionData.documentUrls" :key="idx" 
                  class="flex items-center p-5 bg-white border border-slate-200 rounded-[24px] shadow-sm">
                  <div class="bg-slate-50 p-2.5 rounded-xl mr-5">
                    <!-- 🛡️ FIXED FALLBACK: Removed quotes around DocumentIcon variable -->
                    <component :is="typeof getFileIcon === 'function' ? getFileIcon(url) : DocumentIcon" class="h-6 w-6 text-slate-400" />
                  </div>
                  <span class="text-[11px] font-black text-[#0A1630] truncate flex-1 uppercase tracking-tight">
                    {{ typeof getFileNameFromUrl === 'function' ? getFileNameFromUrl(url) : 'Witness_Document_' + idx }}
                  </span>
               </div>
             </div>
          </div>

          <div v-if="submissionData?.certificationUrls?.length">
             <p class="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-4">Training Certifications</p>
             <div class="space-y-3 mt-4">
               <div v-for="(url, idx) in submissionData.certificationUrls" :key="idx" 
                  class="flex items-center p-5 bg-[#EBFDF8] border border-[#20FCF1]/20 rounded-[24px] shadow-sm">
                  <CheckBadgeIcon class="h-6 w-6 text-[#20FCF1] mr-5" />
                  <span class="text-[11px] font-black text-[#0A1630] truncate flex-1 uppercase tracking-tight">
                    {{ typeof getFileNameFromUrl === 'function' ? getFileNameFromUrl(url) : 'Certification_' + idx }}
                  </span>
               </div>
             </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 6. Official Attestation -->
    <section class="border-t border-slate-100 pt-16 text-left">
      <div class="flex items-center gap-3 mb-10">
        <div class="bg-[#20FCF1] p-2.5 rounded-xl shadow-sm">
          <PencilSquareIcon class="h-6 w-6 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">6. Official Attestation</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
        <!-- Reporter Registry -->
        <div class="bg-white p-10 rounded-[40px] border border-slate-200 shadow-2xl flex flex-col items-center">
          <p class="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-8">Reported By</p>
          <img v-if="submissionData?.reportedBySigDataUrl" :src="submissionData.reportedBySigDataUrl" alt="Reported Signature" class="max-h-24 object-contain mb-8 grayscale" />
          <div class="w-full space-y-3 text-center border-t border-slate-100 pt-8">
            <p class="text-lg font-black text-[#0A1630] uppercase tracking-tight">{{ submissionData?.reportedByName || 'N/A' }}</p>
            <p class="text-[11px] font-bold text-slate-500 uppercase tracking-widest">{{ submissionData?.reportedByCompany || 'TECS' }}</p>
            <p class="text-[11px] font-black text-[#20FCF1] mt-4 tracking-[0.2em] bg-slate-50 inline-block px-4 py-1.5 rounded-full">{{ typeof formatDate === 'function' ? formatDate(submissionData?.reportedByDate) : submissionData?.reportedByDate }}</p>
          </div>
        </div>

        <!-- Witness Registry -->
        <div class="bg-[#0A1630] p-10 rounded-[40px] shadow-2xl flex flex-col items-center text-white border-b-8 border-rose-500">
          <p class="text-[10px] font-black uppercase tracking-[0.3em] text-white/30 mb-8">Witnessed By</p>
          <img v-if="submissionData?.witnessedBySigDataUrl" :src="submissionData.witnessedBySigDataUrl" alt="Witness Signature" class="max-h-24 object-contain mb-8 invert brightness-200" />
          <div class="w-full space-y-3 text-center border-t border-white/10 pt-8">
            <p class="text-lg font-black text-[#20FCF1] uppercase tracking-tight">{{ submissionData?.witnessedByName || 'N/A' }}</p>
            <p class="text-[11px] font-bold text-white/40 uppercase tracking-widest">{{ submissionData?.witnessedByCompany || 'N/A' }}</p>
            <p class="text-[11px] font-black text-white mt-4 tracking-[0.2em] bg-white/5 inline-block px-4 py-1.5 rounded-full">{{ typeof formatDate === 'function' ? formatDate(submissionData?.witnessedByDate) : submissionData?.witnessedByDate }}</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { 
  IdentificationIcon, CalendarDaysIcon, ChatBubbleBottomCenterTextIcon, 
  PaperClipIcon, CheckBadgeIcon, PencilSquareIcon, DocumentIcon
} from '@heroicons/vue/24/outline';

const props = defineProps({
  submissionData: { type: Object, required: true },
  formatDate: { type: Function, required: true },
  formatText: { type: Function, required: true },
  getFileIcon: { type: Function, required: true },
  getFileNameFromUrl: { type: Function, required: true }
});

const hasAttachments = computed(() => {
  const s = props.submissionData;
  if (!s) return false;
  return s.imageUrls?.length || s.documentUrls?.length || s.certificationUrls?.length;
});
</script>

<style scoped>
.StatCard { @apply flex flex-col p-8 bg-white border border-slate-200 rounded-[32px] shadow-sm; }
.StatCard .label { @apply text-[9px] font-black uppercase text-slate-400 tracking-widest mb-2; }
.StatCard .value { @apply text-sm font-black text-[#0A1630] truncate tracking-tight; }
.animate-fadeIn { animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

@media print {
  .bg-slate-50 { background-color: #f8fafc !important; }
  .StatCard { border: 1px solid #e2e8f0 !important; }
  .animate-fadeIn { animation: none !important; opacity: 1 !important; transform: none !important; }
}
</style>